// Doubts: a student asks about a batch (or one class), with photos of their working; the batch's
// teacher (or an admin) answers. Only the student and the batch's staff can see a doubt.
import { Fragment } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, useRoute } from '../router.js';
import { photoUrls, rpc, uploadPhoto } from '../api.js';
import { compressPhoto } from '../photos.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { dateShort, dateTime } from '../format.js';

const STATUS = {
  open: { student: 'Waiting for an answer', staff: 'Needs an answer', tone: 'gold' },
  answered: { student: 'Answered', staff: 'Answered', tone: 'ok' },
  closed: { student: 'Solved', staff: 'Solved', tone: 'plain' },
};

const URL_RE = /(https:\/\/[^\s<>"']+)/g;
function Linked({ text }) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    let url = m[0];
    const trail = url.match(/[.,;:!?)\]]+$/);
    if (trail) url = url.slice(0, -trail[0].length);
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<a href={url} target="_blank" rel="noopener noreferrer" key={m.index}>{url}</a>);
    last = m.index + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Text({ text }) {
  return String(text || '').trim().split(/\n\s*\n/).filter(Boolean).map((p, i) => (
    <p key={i}>{p.split('\n').map((line, j) => <Fragment key={j}>{j ? <br /> : null}<Linked text={line} /></Fragment>)}</p>
  ));
}

/* ---------------------------------------------------------------- Photos */
// Choose up to `max` photos: shown at once from the phone, uploaded when the message is sent.
function PhotoPicker({ photos, setPhotos, max = 3, id }) {
  const input = useRef(null);
  const [err, setErr] = useState(null);
  const pick = (e) => {
    setErr(null);
    const files = Array.from(e.currentTarget.files || []);
    e.currentTarget.value = '';
    const room = max - photos.length;
    if (files.length > room) setErr(`Up to ${max} photos.`);
    const next = files.slice(0, Math.max(0, room)).filter((f) => /^image\//.test(f.type || 'image/')).map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPhotos([...photos, ...next]);
  };
  const remove = (i) => {
    URL.revokeObjectURL(photos[i].url);
    setPhotos(photos.filter((_, j) => j !== i));
  };
  return (
    <div class="photo-picker">
      {photos.length ? (
        <ul class="photo-strip">
          {photos.map((p, i) => (
            <li key={p.url}>
              <img src={p.url} alt={`Photo ${i + 1} to send`} />
              <button type="button" class="photo-remove" onClick={() => remove(i)} aria-label={`Remove photo ${i + 1}`}><Icon name="close" /></button>
            </li>
          ))}
        </ul>
      ) : null}
      {photos.length < max ? (
        <>
          <input ref={input} id={id} hidden type="file" accept="image/*" multiple onChange={pick} />
          <Button variant="line" size="sm" icon="camera" onClick={() => input.current && input.current.click()}>
            {photos.length ? 'Add another photo' : 'Add a photo'}
          </Button>
        </>
      ) : null}
      {err ? <p class="field-error" role="alert">{err}</p> : null}
    </div>
  );
}

async function uploadAll(photos) {
  const paths = [];
  for (const p of photos) paths.push(await uploadPhoto(await compressPhoto(p.file)));
  return paths;
}

function Photos({ paths, urls }) {
  if (!paths || !paths.length) return null;
  return (
    <ul class="photo-grid">
      {paths.map((path, i) => (
        <li key={path}>
          {urls[path]
            ? <a href={urls[path]} target="_blank" rel="noopener"><img src={urls[path]} alt={`Photo ${i + 1}, opens full size`} loading="lazy" /></a>
            : <span class="photo-missing">Photo</span>}
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------- List */
function DoubtRow({ d, staff }) {
  const s = STATUS[d.status] || STATUS.open;
  return (
    <li>
      <Link class={`doubt-row${d.unread ? ' is-unread' : ''}`} href={`/doubts/${d.id}`}>
        <span class="subject">{(d.batch && d.batch.subject) || '•'}</span>
        <span class="doubt-row-text">
          <strong>{d.title}</strong>
          <small>
            {staff && d.student ? `${d.student.name} · ` : ''}{d.batch ? d.batch.name : ''}{d.class ? ` · ${d.class.title}` : ''} · {dateTime(d.last_message_at)}
          </small>
        </span>
        {d.unread ? <span class="dot" aria-label="New reply" /> : null}
        <Chip tone={s.tone}>{staff ? s.staff : s.student}</Chip>
      </Link>
    </li>
  );
}

export function Doubts() {
  const { query } = useRoute();
  const { reloadHome } = useApp();
  const [filter, setFilter] = useState(null);
  const { data, error, loading, reload } = useRpc('my_doubts', { p_status: filter }, [filter]);
  const [asking, setAsking] = useState(false);
  const preset = useMemo(() => ({ batch: query.get('batch') || '', cls: query.get('class') || '' }), []);
  useEffect(() => { if (query.get('ask') === '1') setAsking(true); }, []);
  useEffect(() => { reloadHome(true); }, []);
  const staff = data && data.staff;
  return (
    <Shell title="Doubts" actions={data && data.can_ask ? <Button variant="gold" size="sm" icon="plus" onClick={() => setAsking(true)}>Ask a doubt</Button> : null}>
      <p class="muted">{staff ? 'Questions from students in your batches. Only the student and the batch’s teachers see them.' : 'Ask about anything from your classes. Only you and your teachers see your doubts.'}</p>
      {staff ? (
        <div class="filters" role="group" aria-label="Show">
          {[[null, 'All'], ['open', 'Needs an answer'], ['answered', 'Answered'], ['closed', 'Solved']].map(([v, l]) => (
            <button type="button" key={l} class={`quick-btn${filter === v ? ' is-on' : ''}`} aria-pressed={filter === v ? 'true' : 'false'} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
      ) : null}
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.doubts.length ? (
        <Empty icon="chat" title={staff ? 'No doubts here' : 'No doubts yet'}
          action={data.can_ask ? <Button variant="navy" icon="plus" onClick={() => setAsking(true)}>Ask a doubt</Button> : null}>
          {staff ? 'When students ask, their questions appear here.' : 'Stuck on a sum or a concept? Ask here, with a photo of your working if that helps.'}
        </Empty>
      ) : null}
      {data && data.doubts.length ? <ul class="list doubt-list">{data.doubts.map((d) => <DoubtRow key={d.id} d={d} staff={staff} />)}</ul> : null}
      {data && data.can_ask ? (
        <AskDoubt open={asking} batches={data.batches} preset={preset} onClose={() => setAsking(false)}
          onSaved={(id) => { reload(true); navigate(`/doubts/${id}`); }} />
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Ask */
function AskDoubt({ open, batches, preset, onClose, onSaved }) {
  const toast = useToast();
  const first = (batches.find((b) => b.id === preset.batch) || batches[0] || {}).id || '';
  const [f, setF] = useState({ batch: first, cls: preset.cls, title: '', body: '' });
  const [photos, setPhotos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const kept = useRef([]);
  kept.current = photos;
  const clearPhotos = () => { kept.current.forEach((p) => URL.revokeObjectURL(p.url)); setPhotos([]); };
  useLayoutEffect(() => {
    if (!open) { clearPhotos(); return; }
    setErr(null);
    setF({ batch: first, cls: preset.cls, title: '', body: '' });
  }, [open]);
  useEffect(() => () => kept.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  useEffect(() => { // the classes of the chosen batch, to pick the one the doubt is about
    if (!open || !f.batch) { setClasses([]); return; }
    let live = true;
    rpc('get_batch', { p_batch_id: f.batch }).then((b) => { if (live) setClasses(b.classes.filter((c) => !c.is_loose)); }).catch(() => setClasses([]));
    return () => { live = false; };
  }, [open, f.batch]);
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.value });

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!f.body.trim() && !photos.length) { setErr('Write your question or add a photo.'); return; }
    try {
      setBusy(photos.length ? 'Uploading photos…' : 'Sending…');
      const paths = await uploadAll(photos);
      setBusy('Sending…');
      const res = await rpc('ask_doubt', { p_batch_id: f.batch, p_class_id: f.cls || null, p_title: f.title, p_body: f.body, p_photos: paths });
      clearPhotos();
      toast('Sent to your teacher');
      onClose();
      onSaved(res.id);
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };

  return (
    <Sheet title="Ask a doubt" open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        {batches.length > 1 ? (
          <Field label="Batch">{(id) => (
            <select id={id} value={f.batch} onChange={(e) => setF({ ...f, batch: e.currentTarget.value, cls: '' })}>
              {batches.map((b) => <option value={b.id} key={b.id}>{b.name}</option>)}
            </select>
          )}</Field>
        ) : null}
        <Field label="Which class is it about? (optional)">{(id) => (
          <select id={id} value={f.cls} onChange={set('cls')}>
            <option value="">Not a particular class</option>
            {classes.map((c) => <option value={c.id} key={c.id}>{c.class_date ? `${dateShort(c.class_date)}: ` : ''}{c.title}</option>)}
          </select>
        )}</Field>
        <Field label="Your question">{(id) => (
          <textarea id={id} rows={5} maxLength={4000} value={f.body} onInput={set('body')}
            placeholder="Which step don't you follow? Say what you tried." />
        )}</Field>
        <Field label="Short title (optional)" hint="If you leave it empty, the first line of your question is used.">{(id) => (
          <input id={id} type="text" maxLength={120} value={f.title} onInput={set('title')} />
        )}</Field>
        <div class="field" role="group" aria-labelledby="ask-photos-label">
          <span class="field-label" id="ask-photos-label">Photos of your working (up to 3)</span>
          <PhotoPicker photos={photos} setPhotos={setPhotos} id="ask-photos" />
        </div>
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions">
          <Button type="submit" variant="navy" icon="send" busy={Boolean(busy)}>{busy || 'Send'}</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------------------------------------------------------------- One doubt */
export function DoubtThread({ id }) {
  const toast = useToast();
  const { reloadHome } = useApp();
  const { data, error, loading, reload } = useRpc('get_doubt', { p_doubt_id: id }, [id]);
  const [urls, setUrls] = useState({});
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const kept = useRef([]);
  kept.current = photos;
  useEffect(() => () => kept.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  useEffect(() => {
    if (!data) return;
    reloadHome(true); // opening it marked it read
    const paths = data.thread.flatMap((m) => m.photos || []);
    if (paths.length) photoUrls(paths).then(setUrls);
  }, [data]);

  const send = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!body.trim() && !photos.length) { setErr('Write a reply or add a photo.'); return; }
    try {
      setBusy(photos.length ? 'Uploading photos…' : 'Sending…');
      const paths = await uploadAll(photos);
      setBusy('Sending…');
      await rpc('reply_doubt', { p_doubt_id: id, p_body: body, p_photos: paths });
      setBody('');
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      toast(data.can_answer ? 'Answer sent' : 'Reply sent');
      reload(true);
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };
  const setStatus = async (status) => {
    try {
      await rpc('set_doubt_status', { p_doubt_id: id, p_status: status });
      toast(status === 'closed' ? 'Marked as solved' : 'Opened again');
      reload(true);
    } catch (ex) { toast(ex.message, 'bad'); }
  };

  const d = data;
  const s = d ? STATUS[d.status] || STATUS.open : null;
  return (
    <Shell backTo="/doubts" backLabel="Doubts">
      {loading && !d ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {d ? (
        <article class="doubt">
          <p class="eyebrow">{d.batch.name}{d.class ? ` · ${d.class.title}` : ''}</p>
          <h1 class="page-title">{d.title}</h1>
          <p class="doubt-meta">
            <Chip tone={s.tone}>{d.can_answer ? s.staff : s.student}</Chip>
            {d.can_answer ? <span>Asked by {d.student.name}</span> : null}
            <span>{dateTime(d.created_at)}</span>
          </p>
          <ol class="thread">
            {d.thread.map((m) => (
              <li key={m.id} class={`message${m.author.staff ? ' is-staff' : ''}${m.mine ? ' is-mine' : ''}`}>
                <p class="message-head">
                  <strong>{m.mine ? 'You' : m.author.name}</strong>
                  {m.author.staff ? <Chip tone="navy">Teacher</Chip> : null}
                  <span>{dateTime(m.created_at)}</span>
                </p>
                {m.body ? <div class="message-body"><Text text={m.body} /></div> : null}
                <Photos paths={m.photos} urls={urls} />
              </li>
            ))}
          </ol>
          <form class="reply card form" onSubmit={send}>
            <Field label={d.can_answer ? 'Your answer' : 'Reply'}>{(fid) => (
              <textarea id={fid} rows={4} maxLength={4000} value={body} onInput={(e) => setBody(e.currentTarget.value)} />
            )}</Field>
            <PhotoPicker photos={photos} setPhotos={setPhotos} id="reply-photos" />
            {err ? <p class="field-error" role="alert">{err}</p> : null}
            <div class="form-actions">
              <Button type="submit" variant="navy" icon="send" busy={Boolean(busy)}>{busy || (d.can_answer ? 'Send answer' : 'Send')}</Button>
              {d.status === 'closed'
                ? <Button variant="ghost" onClick={() => setStatus('open')}>Open again</Button>
                : <Button variant="ghost" icon="check" onClick={() => setStatus('closed')}>Mark as solved</Button>}
            </div>
          </form>
        </article>
      ) : null}
    </Shell>
  );
}
